// NOTIF-INST-01 · suscripción Web Push de ESTE dispositivo (iPad o PC institucional).
// El permiso SÓLO se pide desde un gesto del usuario (iPadOS lo exige y es la política de Galarya).
// El vínculo que se guarda en el navegador se considera válido únicamente cuando el SERVIDOR
// confirma que ese endpoint sigue registrado para el usuario y la sesión vigentes.
import api from "@/lib/api";

const SW_URL = "/push-sw.js";
const VINCULO = "galarya_push_vinculo"; // { userId, endpoint }

export const soportado = () =>
  typeof window !== "undefined" && window.isSecureContext &&
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

function leerVinculo() {
  try { return JSON.parse(window.localStorage?.getItem(VINCULO) || "null"); } catch { return null; }
}
const guardarVinculo = (userId, endpoint) =>
  window.localStorage?.setItem(VINCULO, JSON.stringify({ userId, endpoint }));
const borrarVinculo = () => window.localStorage?.removeItem(VINCULO);

// Un push CONFIRMADO para este usuario y este dispositivo sustituye al aviso local: no se duplican.
// Una bandera vieja de otra sesión o de otro usuario NO silencia nada.
export const pushActivoLocal = (userId) => {
  const v = leerVinculo();
  return Boolean(v && userId && v.userId === userId &&
    typeof Notification !== "undefined" && Notification.permission === "granted");
};

function bytesDeBase64Url(valor) {
  const pad = "=".repeat((4 - (valor.length % 4)) % 4);
  const crudo = atob((valor + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...crudo].map((c) => c.charCodeAt(0)));
}

async function registro() {
  const r = await navigator.serviceWorker.register(SW_URL);
  await navigator.serviceWorker.ready;
  return r;
}

async function suscripcionLocal() {
  try {
    const r = await navigator.serviceWorker.getRegistration(SW_URL);
    return r ? await r.pushManager.getSubscription() : null;
  } catch {
    return null;
  }
}

async function registrarEnServidor(sub) {
  const j = sub.toJSON();
  await api.put("/push/suscripciones", { endpoint: j.endpoint, keys: j.keys });
}

/** Reconcilia el navegador con el REGISTRO DEL SERVIDOR para este usuario y sesión.
 *  Si el dispositivo tiene permiso y suscripción pero el servidor ya no la reconoce (nueva sesión,
 *  revocación, caducidad o baja), se vuelve a vincular con la sesión vigente; si no se puede, se
 *  declara «requiere_activacion» en vez de fingir que sigue activa. */
export async function estado(userId) {
  if (!soportado()) { borrarVinculo(); return { estado: "no_disponible", permiso: "no_soportado" }; }
  const permiso = Notification.permission;
  if (permiso !== "granted") { borrarVinculo(); return { estado: permiso === "denied" ? "denegada" : "inactiva", permiso }; }

  const sub = await suscripcionLocal();
  if (!sub) { borrarVinculo(); return { estado: "inactiva", permiso }; }

  try {
    const { data } = await api.get("/push/estado", { params: { endpoint: sub.endpoint } });
    if (data?.vinculada) { guardarVinculo(userId, sub.endpoint); return { estado: "activada", permiso }; }
    await registrarEnServidor(sub);
    guardarVinculo(userId, sub.endpoint);
    return { estado: "activada", permiso };
  } catch {
    borrarVinculo();
    return { estado: "requiere_activacion", permiso,
             mensaje: "Vuelve a activar las notificaciones en este dispositivo." };
  }
}

export async function activar(userId) {
  if (!soportado()) return { estado: "no_disponible", mensaje: "Este dispositivo no admite notificaciones web. En iPad, añade Galarya a la pantalla de inicio y ábrela desde ahí." };
  const permiso = await Notification.requestPermission();
  if (permiso !== "granted") {
    borrarVinculo();
    return { estado: permiso === "denied" ? "denegada" : "inactiva",
             mensaje: "No se concedió el permiso de notificaciones en este dispositivo." };
  }
  const { data } = await api.get("/push/clave-publica");
  if (!data?.disponible || !data?.clave) {
    return { estado: "no_disponible", mensaje: "El servidor aún no tiene configuradas las notificaciones." };
  }
  const r = await registro();
  let sub = await r.pushManager.getSubscription();
  if (!sub) {
    sub = await r.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: bytesDeBase64Url(data.clave),
    });
  }
  await registrarEnServidor(sub);
  guardarVinculo(userId, sub.endpoint);
  return { estado: "activada", mensaje: "Notificaciones activadas en este dispositivo." };
}

export async function desactivar() {
  borrarVinculo();
  if (!soportado()) return { estado: "no_disponible" };
  const sub = await suscripcionLocal();
  if (sub) {
    await api.post("/push/suscripciones/baja", { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  return { estado: "inactiva", mensaje: "Notificaciones desactivadas en este dispositivo." };
}

/** Cierre de sesión: se da de baja ÚNICAMENTE el equipo que cierra. Los demás dispositivos de la
 *  misma cuenta (otro iPad, un iPhone o la PC) conservan su suscripción. Un navegador sin Push no
 *  llama a nada. Debe completarse ANTES de borrar el token, por eso quien lo llama lo espera. */
export async function limpiarEnLogout() {
  borrarVinculo();
  if (!soportado()) return;
  const sub = await suscripcionLocal();
  if (!sub) return;
  try {
    await api.post("/push/suscripciones/baja", { endpoint: sub.endpoint });
  } catch { /* cerrar sesión nunca se bloquea por esto */ }
  try {
    await sub.unsubscribe();            // independiente del resultado de la API
  } catch { /* el navegador puede haberla retirado ya */ }
}
